/* ============
 * Vuex Store
 * ============
 *
 * The store of the application.
 *
 * http://vuex.vuejs.org/en/index.html
 */

import Vue from 'vue';
import Vuex from 'vuex';
import sharedMutation from 'vuex-shared-mutations';

import $http from '@/api';
import isArray from 'lodash/isArray';
import { wizards as defaultEntityWizards, searchLimitConfig } from '@/modules/searcher/config';
import locales from '@/locale';

// Modules
import authentication from '@/modules/authentication/store';
import authorization from '@/modules/authorization/store';
import dayparting from '@/modules/board/dayparting/store';
import content from '@/modules/content/store';
import live from '@/modules/live-messages/store';
import entity from '@/modules/entity/store';
import treeView from '@/modules/entity/editor/tree-view/store';
import upload from '@/modules/upload/store';
import room from '@/modules/room/store';
import toast from '@/modules/toast/store';
import dialog from '@/modules/app-dialog/store';
import scrollytelling from '@/modules/scrollytelling/store';
import searcher from '@/modules/searcher/store';
import bag from '@/modules/bag/store';
import milenium from '@/modules/milenium/store';
import dashboard from '@/modules/dashboard/store';
import activity from '@/modules/activity/store';
import staff from '@/modules/staff/store';
import staffNotification from '@/modules/staff-notification/store/index.ts';
import onboarding from '@/modules/onboarding/store';

// OverlordJS
import feathersVuex from 'feathers-vuex';
import overlord from '@/overlord';

// Features
import features, { DASHBOARD_FEATURE } from '@/features';
import { aspectRatios } from '@/modules/clipping/config';

import { entityMetadata, transpose } from '@/utils';
import bugsnag from '@/plugins/bugsnag';
import router from '@/router';
import { i18n } from '@/plugins/vue-i18n';
import Validator from '@/plugins/vee-validate';

// App global events
import { AFTER_SITE_CHANGE_EVENT, BEFORE_SITE_CHANGE_EVENT, eventBus } from '@/events';
import merge from 'lodash/merge';
import get from 'lodash/get';

const { makeAuthPlugin, FeathersVuex } = feathersVuex(overlord);

Vue.use(Vuex);
Vue.use(FeathersVuex);

const debug = process.env.NODE_ENV !== 'production';

let loadingBootstrapPromise = null;

export default new Vuex.Store({
  state: {
    bootstrap: {
      environmentDecorator: null,
      passwordValidationConfig: {},
      staffGoogleLoginClientId: null,
      staffPasswordRecoveryEnabled: true,
    },
    app: {
      connectionId: overlord.connectionId,
      drawer: false,
      notifications: false,
      bag: false,
      activeFullscreenDialogs: [],
    },
    locale: {
      current: i18n.locale,
      available: [i18n.locale],
    },
    loading: {
      bootstrap: true,
      app: true, // Loading app configuration
      site: false, // Updating current site
      view: true,
      viewChangeStarts: 0, // view change start to compare vs threshold
      threshold: 250, // Minimum loading time for view change
    },
    config: {
      multilangMode: null,
      overlordToken: null,
      overlordReplyCourtesyTime: 10000,
      loggedStaff: {},
      staffPreferences: {},
      sites: {},
      navigation: [],
      entities: {},
      wizards: {},
      seo: {},
      content: {
        preloadPreview: true,
        types: [],
        hasLandingPage: [],
      },
      clippings: {
        beforeCreateAspectRatios: { ...aspectRatios },
        availableAspectRatios: { ...aspectRatios },
        availableClippingsInBodyAliases: null,
      },
      clipboard: {
        id: null,
      },
      features: { ...features },
      socialNetwork: {},
      bcubeAssistant: {},
      searcherConfig: {},
    },
    site: {},
    profile: {
      onboarding: {
        lastFeaturedViewed: null,
        dashboardRevertHomepageShown: false,
      },
    },
  },
  /**
   * Assign the modules to the store.
   */
  modules: {
    authentication,
    authorization,
    dayparting,
    content,
    composition: {
      namespaced: true,
    },
    live,
    entity,
    treeView,
    upload,
    room,
    toast,
    dialog,
    scrollytelling,
    searcher,
    bag,
    milenium,
    dashboard,
    activity,
    staff,
    staffNotification,
    onboarding,
  },
  mutations: {
    SET_LOADING_APP: (state, status) => {
      // Force status to be boolean
      state.loading.app = !!status;
    },
    SET_LOADING_SITE: (state, status) => {
      // Force status to be boolean
      state.loading.site = !!status;
    },
    SET_LOADING_VIEW: (state, status) => {
      state.loading.view = !!status;
      state.loading.viewChangeStarts = new Date();
    },
    SET_LOADING_BOOTSTRAP: (state, loading) => {
      state.loading.bootstrap = loading;
    },
    SET_BOOTSTRAP: (state, bootstrap) => {
      state.bootstrap = merge({}, state.bootstrap, bootstrap);
    },
    SET_CONFIG: (state, config) => {
      config.features = {
        ...state.config.features,
        ...(config.features || {}),
      };
      state.config = config;
    },
    SET_OVERLORD_TOKEN: (state, token) => {
      state.config.overlordToken = token;
    },
    SET_SITE: (state, site) => {
      state.site = site;
      state.siteId = site.id;
    },
    SET_BAG: (state, value) => {
      state.app.bag = value;
    },
    SET_DRAWER: (state, value) => {
      state.app.drawer = value;
    },
    SET_NOTIFICATIONS: (state, value) => {
      state.app.notifications = value;
    },
    SET_ACTIVE_FULLSCREEN_DIALOGS: (state, value) => {
      state.app.activeFullscreenDialogs = value;
    },
    SET_PROFILE_DATA(state, cmsCustomData) {
      merge(state.profile, cmsCustomData);
      state.profile.onboarding.lastFeaturedViewed = state.profile.onboarding.lastFeaturedViewed || 0;
    },
    SET_LOCALE(state, locale) {
      state.locale.current = locale;
    },
    SET_AVAILABLE_LOCALES(state, available) {
      state.locale.available = available;
    },
  },
  actions: {
    UPDATE_LOADING_VIEW({ commit }, value) {
      commit('SET_LOADING_VIEW', value);
    },

    /**
     * Loads the CMS's bootstrap configuration if it hasn't already been loaded.
     * @return {Promise<null>}
     */
    async LOAD_BOOTSTRAP({ dispatch }) {
      if (!loadingBootstrapPromise) {
        loadingBootstrapPromise = dispatch('INTERNAL_LOAD_BOOTSTRAP');
      }
      return loadingBootstrapPromise;
    },
    /**
     * Loads the CMS's bootstrap configuration.
     * @return {Promise<void>}
     */
    async INTERNAL_LOAD_BOOTSTRAP({ commit }) {
      try {
        commit('SET_LOADING_BOOTSTRAP', true);
        const response = await $http.get('/api/bootstrap');
        await commit('SET_BOOTSTRAP', response);
      } catch (error) {
        console.error('Error loading bootstrap');
        throw error;
      } finally {
        commit('SET_LOADING_BOOTSTRAP', false);
      }
    },

    async LOAD_BASE_CONFIG({ dispatch, commit, getters }, siteAlias) {
      try {
        commit('SET_LOADING_APP', true);
        console.debug('Bugsnag instance', bugsnag);
        const response = await $http.get('/api/config');
        // Configure bugsnag environment if present
        if (response.environment) {
          bugsnag.config.releaseStage = response.environment;
        }
        response.loggedStaff.avatarUrl = response.loggedStaff.avatarUrl || 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEgAAABICAQAAAD/5HvMAAABVElEQVR4Ae3XJUKgURRA4TsSxyeNuyAJFsACcMs4NByWgjc047YBCpJwaHjC/ZBxHvz3YfdbwUlXxBhjjHnceE8WHYyxzhpjtJPNW7ktfKOKXU7aoYrP4h/xbHGeLeLFLwq4TKH4QwyXOyRO/OAjm1zFJp/EBxq5qgbRxw9c/BBtFOOiWLTRh4s+0cYkLiZFG3u4WBdtrN+1oClcTIk2+nHRL9oowUWJaOMnLn7drdXRKD7wyXG56iOOq4gRfyjiMkXiF0nscJ4d4u3IP+MNGqfN/xtkjPGGEFIop5I+ZjgA4IAZeqminFRCxBf+kUcLi1xmkWby+Cd6iKCKeVzNU0mkBIvnpDPETQyRznMJBlFMEIQJouSmeEktQarjpVwfYcwStFnC5Xr4ygoalvkm7njKCFqGeSauyEFTjrhiAE0D4ootNG2JK5RZkAVZkDHGGHNPHAFI9mlF2ahd1AAAAABJRU5ErkJggg==';
        // Auth in OverlordJS
        await dispatch('auth/authenticate', {
          strategy: 'jwt',
          accessToken: response.overlordToken,
        });

        dispatch('PATCH_CONFIG', {
          overlordToken: response.overlordToken,
          overlordReplyCourtesyTime: response.overlordReplyCourtesyTime || 10000,
          loggedStaff: response.loggedStaff,
          sites: response.sites,
          multilangMode: response.multilangMode,
        });

        // Configure the current bugsnag user
        bugsnag.user = getters.loggedStaff;

        let siteId = getters.defaultSite.id;
        if (siteAlias
          && getters.getSiteFromIdAlias(siteAlias)) {
          siteId = getters.getSiteFromIdAlias(siteAlias).id;
        }

        await dispatch('SET_SITE', siteId);
        const promises = [];

        // Load current staff saved searches
        if (getters.features.SAVED_SEARCHES_FEATURE) {
          promises.push(dispatch('searcher/FETCH_SAVED_SEARCHES'));
        }

        // Fetch user profile data
        promises.push(dispatch('FETCH_PROFILE_DATA'));
        // Fetch user notifications
        if (getters.features.STAFF_NOTIFICATIONS) {
          promises.push(dispatch('staffNotification/LOAD_STAFF_NOTIFICATIONS'));
        }
        await Promise.all(promises);

        // Configure user locale
        let userLocale = localStorage.getItem('locale');

        // User has no locale selected or the selected one is no longer supported for the project
        if (!userLocale || !getters.availableLocales.map(l => l.code).includes(userLocale)) {
          // If there are no locales available (should not happen, better safe than sorry) default to the
          // one configured by default
          userLocale = getters.availableLocales[0] ? getters.availableLocales[0].code : i18n.locale;

          // Update localstorage to have a valid state
          localStorage.setItem('locale', userLocale);
        }

        dispatch('SET_LOCALE', userLocale);

        commit('SET_LOADING_APP', false);
      } catch (error) {
        console.error('Error loading config');
        throw error;
      }
    },

    async LOAD_SITE_CONFIG({
      state, dispatch, commit, getters,
    }, siteId) {
      try {
        const loadingNewSiteId = state.site.id !== siteId;
        if (loadingNewSiteId) {
          commit('SET_LOADING_APP', true);
        }
        const response = await $http.get(`/api/config/${siteId}`);
        response.loggedStaff.avatarUrl = response.loggedStaff.avatarUrl || 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEgAAABICAQAAAD/5HvMAAABVElEQVR4Ae3XJUKgURRA4TsSxyeNuyAJFsACcMs4NByWgjc047YBCpJwaHjC/ZBxHvz3YfdbwUlXxBhjjHnceE8WHYyxzhpjtJPNW7ktfKOKXU7aoYrP4h/xbHGeLeLFLwq4TKH4QwyXOyRO/OAjm1zFJp/EBxq5qgbRxw9c/BBtFOOiWLTRh4s+0cYkLiZFG3u4WBdtrN+1oClcTIk2+nHRL9oowUWJaOMnLn7drdXRKD7wyXG56iOOq4gRfyjiMkXiF0nscJ4d4u3IP+MNGqfN/xtkjPGGEFIop5I+ZjgA4IAZeqminFRCxBf+kUcLi1xmkWby+Cd6iKCKeVzNU0mkBIvnpDPETQyRznMJBlFMEIQJouSmeEktQarjpVwfYcwStFnC5Xr4ygoalvkm7njKCFqGeSauyEFTjrhiAE0D4ootNG2JK5RZkAVZkDHGGHNPHAFI9mlF2ahd1AAAAABJRU5ErkJggg==';
        const forms = response.forms || {};
        const clippings = { ...state.config.clippings };

        if (response.clippings) {
          if (response.clippings.availableClippings
            && isArray(response.clippings.availableClippings)) {
            // Transpose clippings by alias
            const availableAspectRatios = transpose(response.clippings.availableClippings, 'alias');
            // Backward compatibility. beforeCreateAspectRatios are the same as availableAspectRatios
            const beforeCreateAspectRatios = availableAspectRatios;
            Object.assign(clippings, { availableAspectRatios, beforeCreateAspectRatios });
          }

          if (response.clippings.beforeCreateClippings
            && isArray(response.clippings.beforeCreateClippings)) {
            // Transpose clippings by alias
            const beforeCreateAspectRatios = transpose(response.clippings.beforeCreateClippings, 'alias');
            Object.assign(clippings, { beforeCreateAspectRatios });
          }

          if (response.clippings.availableClippingsInBodyAliases) {
            const availableClippingsInBodyAliases = response.clippings.availableClippingsInBodyAliases;
            Object.assign(clippings, { availableClippingsInBodyAliases });
          }
        }

        commit('SET_CONFIG', Object.assign({}, {
          multilangMode: state.config.multilangMode,
          overlordToken: {},
          loggedStaff: {},
          staffPreferences: {},
          sites: [],
          navigation: {},
          scrollytelling: {},
          searcher: {},
          searcherConfig: {
            searchLimitConfig,
          },
          entities: forms.entities || {},
          wizards: forms.wizards || {},
          seo: {},
          content: {
            preloadPreview: true,
            types: [],
            hasLandingPage: [],
          },
          socialNetwork: {},
          clippings: state.config.clippings,
          clipboard: state.config.clipboard || { id: null },
          features: state.config.features,
        }, response, { clippings }));
      } catch (error) {
        console.error(`Error loading config for siteId ${siteId}`, error);
        if (error.response.status === 403) {
          router.push({ name: 'unauthorized', path: null });
        } else {
          throw error;
        }
      } finally {
        commit('SET_LOADING_APP', false);
      }
    },

    async SET_SITE({ commit, dispatch, getters }, siteId) {
      const site = getters.availableSites.find(item => item.id === siteId);
      eventBus.$emit(BEFORE_SITE_CHANGE_EVENT, site);

      commit('SET_LOADING_SITE', true);
      await dispatch('LOAD_SITE_CONFIG', siteId);
      commit('SET_SITE', site);

      if (getters.features[DASHBOARD_FEATURE]) {
        await dispatch('dashboard/FETCH_DASHBOARD_DATA');
      }
      // Configure bugsnag context
      bugsnag.metaData = { context: getters.context };

      commit('SET_LOADING_SITE', false);
      eventBus.$emit(AFTER_SITE_CHANGE_EVENT, site);
    },

    TOGGLE_DRAWER({ state, commit }, value = null) {
      if (value !== null) {
        commit('SET_DRAWER', !!value);
      } else {
        commit('SET_DRAWER', !state.app.drawer);
      }
    },

    TOGGLE_NOTIFICATIONS({ state, commit }, value = null) {
      if (value !== null) {
        commit('SET_NOTIFICATIONS', !!value);
      } else {
        commit('SET_NOTIFICATIONS', !state.app.notifications);
      }
    },

    TOGGLE_BAG({ state, commit }, value = null) {
      if (value !== null) {
        commit('SET_BAG', !!value);
      } else {
        commit('SET_BAG', !state.app.bag);
      }
    },

    PATCH_CONFIG({ state, commit }, patch) {
      commit('SET_CONFIG', Object.assign({}, state.config, patch));
    },

    FULLSCREEN_DIALOG_OPENED({ state, commit }, id) {
      if (state.app.activeFullscreenDialogs.includes(id)) return;
      commit('SET_ACTIVE_FULLSCREEN_DIALOGS', [...state.app.activeFullscreenDialogs, id]);
    },

    FULLSCREEN_DIALOG_CLOSED({ state, commit }, id) {
      commit('SET_ACTIVE_FULLSCREEN_DIALOGS', state.app.activeFullscreenDialogs.filter(i => i !== id));
    },

    async SYNC_PROFILE_DATA({ state, getters }) {
      try {
        await $http.patch(`/api/profile/${getters.loggedStaff.id}/editor`, { cmsCustomData: JSON.stringify(state.profile) });
      } catch (e) {
        console.error('Error while sync profile data', e);
      }
    },

    async FETCH_PROFILE_DATA({ commit, dispatch, getters }) {
      const { cmsCustomData = '{}' } = await $http.get(`/api/profile/${getters.loggedStaff.id}/editor`);
      try {
        commit('SET_PROFILE_DATA', JSON.parse(cmsCustomData) || {});
        dispatch('onboarding/INITIALIZE_ONBOARDING_MANAGER');
      } catch (e) {
        console.error('Wrong data in profile cmsCustomData field', cmsCustomData);
      }
    },

    UPDATE_ONBOARDING_LAST_FEATURE_VIEWED({ commit, dispatch }, featureId) {
      commit('SET_PROFILE_DATA', {
        onboarding: {
          lastFeaturedViewed: featureId,
        },
      });
      return dispatch('SYNC_PROFILE_DATA');
    },

    async UPDATE_DASHBOARD_REVERT_HOMEPAGE_SHOWN({ commit, getters, state }, { dashboardRevertHomepageShown, homepage }) {
      commit('SET_PROFILE_DATA', { onboarding: { dashboardRevertHomepageShown } });

      const payload = {};
      payload.cmsCustomData = JSON.stringify(state.profile);
      if (homepage) {
        payload.homepage = homepage;
      }

      return $http.patch(`/api/profile/${getters.loggedStaff.id}/editor`, payload)
        .catch(e => console.error('Error while sync profile data', e));
    },

    async UPDATE_STAFF_PREFERENCES({ commit, dispatch }, payload) {
      try {
        const { preferences } = await $http.put('/api/staff/preferences', { preferences: payload });
        dispatch('PATCH_CONFIG', { staffPreferences: preferences });
        return { preferences };
      } catch (e) {
        console.error('Error while update staff preferences ', e, { preferences: payload });
        return null;
      }
    },

    SET_LOCALE({ commit }, locale) {
      commit('SET_LOCALE', locale);
      i18n.locale = locale;
      localStorage.setItem('locale', locale);
      Validator.localize(locale);
    },

    SET_AVAILABLE_LOCALES({ commit }, available) {
      commit('SET_AVAILABLE_LOCALES', available);
    },
  },
  getters: {
    loadingBootstrap: state => state.loading.bootstrap,
    bootstrap: state => state.bootstrap,
    oAuthEnabled: state => !!state.bootstrap.staffGoogleLoginClientId,
    staffPasswordRecoveryEnabled: state => state.bootstrap.staffPasswordRecoveryEnabled,

    loading: state => state.loading,
    loadingApp: state => state.loading.app || state.loading.site,
    loadingView: state => state.loading.view,
    navigation: state => state.config.navigation,
    availableLocales: state => state.locale.available.map(code => ({ code, ...locales[code] })),
    /*
     * Site and context getters
     */
    site: state => state.site || {},
    siteId: state => state.site.id || null,
    availableMedias: state => state.config.sites.available || [],
    availableSites: (state, getters) => getters.availableMedias.reduce((sites, media) => sites
      .concat(media.sites
        .map(site => ({ ...site, media: media.name })) || []), []),
    allowedSites: (state, getters) => getters.availableSites
      .filter(site => state.config.sites.allowed
        .includes(site.id)),
    getSiteFromSiteId: (state, getters) => siteId => getters
      .availableSites
      .find(site => site.id === siteId) || null,
    getMediaIdSites: (state, getters) => mediaId => getters.availableMedias.reduce((sites, media) => {
      if (media.id === mediaId) {
        return sites.concat(media.sites);
      }
      return sites;
    }, []),
    getSiteFromIdAlias: (state, getters) => idAlias => getters
      .availableSites
      .find(site => site.idAlias === idAlias) || null,
    defaultSite: (state, getters) => getters.getSiteFromSiteId(state.config.sites.default),
    context: state => ({
      sourceCode: 'bCube', // CMS Source Code
      siteId: state.site.id || null,
    }),

    loggedStaff: state => state.config.loggedStaff,
    authorization: state => state.config.authorization || {},
    searcher: state => state.config.searcher,
    searcherConfig: state => state.config.searcherConfig,
    searchLimitConfigForEntity: state => entityName => get(state.config, `searcher.${entityName}.searchLimitConfig`, null) || state.config.searcherConfig.searchLimitConfig,
    searcherTabs: state => type => state.config.searcher[type].tabs,
    getEntityConfig: state => entityName => ({
      ...state.config.entities[entityName],
      metadata: entityMetadata(entityName),
    }) || null,
    availableSearcherWizards: (state, getters) => (entityName) => {
      const entitySearcherConfig = getters.searcher[entityName];
      if (entitySearcherConfig && entitySearcherConfig.wizards) {
        return entitySearcherConfig.wizards;
      }
      return [];
    },
    getWizard: state => (name) => {
      if (!name) {
        return null;
      }
      const wizard = state.config.wizards[name] || null;
      if (wizard) {
        return wizard;
      }
      const configuredWizard = defaultEntityWizards[name];
      if (configuredWizard) {
        return configuredWizard;
      }
      return null;
    },
    getSearcherApiPath: (state, getters) => (entityName) => {
      const config = getters.searcher[entityName];
      if (!config || !config.apiPath) {
        return null;
      }
      return config.apiPath;
    },
    getEntityApiPath: (state, getters) => (entityName) => {
      const config = getters.getEntityConfig(entityName);
      if (!config || !config.apiPath) {
        return null;
      }
      return config.apiPath;
    },
    // CMS available content types
    availableContentTypes: (state) => {
      if (state.config.content && state.config.content.types) {
        return state.config.content.types;
      }
      return [];
    },
    // CMS available entity types
    availableEntityTypes: (state, getters) => {
      if (!state.config.entities) {
        return [];
      }
      const types = Object.keys(state.config.entities) || [];
      return types.filter(type => !getters.availableContentTypes.includes(type));
    },
    // CMS contents with landing page
    hasLandingPageContentTypes: (state) => {
      if (state.config.content && state.config.content.hasLandingPage) {
        return state.config.content.hasLandingPage;
      }
      return [];
    },
    currentContext: (state, getters) => ({
      siteId: getters.site.id || null,
      siteName: getters.site.name || null,
      staffId: getters.loggedStaff.id || null,
      staffName: getters.loggedStaff.name || null,
    }),
    // CMS available clippings. Configured by site
    beforeCreateClippingAspectRatios: state => state.config.clippings.beforeCreateAspectRatios,
    beforeCreateClippingAspectRatioAliases: state => Object.keys(state.config.clippings.beforeCreateAspectRatios),
    availableClippingAspectRatios: state => state.config.clippings.availableAspectRatios,
    availableClippingAspectRatioAliases: state => Object.keys(state.config.clippings.availableAspectRatios),
    availableClippingAspectRatioInBodyAliases: (state, getters) => {
      if (state.config.clippings.availableClippingsInBodyAliases) {
        return state.config.clippings.availableClippingsInBodyAliases;
      }

      return getters.availableClippingAspectRatioAliases;
    },
    // CMS feature flags
    features: state => state.config.features,
    publicClipboardId: state => state.config.clipboard.id,
    bcubeAssistant: state => state.config.bcubeAssistant,
  },
  /**
   * If strict mode should be enabled.
   */
  strict: debug,
  /*
   * Plugins
   */
  plugins: [
    // Messages endpoint
    // service('messages'),
    // Setup the auth plugin.
    makeAuthPlugin({ userService: 'users' }),
    // Lo declaramos en la store general ya que Vuex no permite
    // registrar plugins en módulos https://github.com/vuejs/vuex/issues/467
    sharedMutation({
      predicate: ['bag/clipboard/SET_PRIVATE_CLIPBOARD_ITEMS'],
    }),
  ],
});
